import { ICondition } from '@kurtosys/types/common/ICondition.js';
import { IConditional } from '@kurtosys/types/common/IConditional.js';
import IMatchesWithOptions from '@kurtosys/types/helpers/ConditionalHelper/IMatchesWithOptions.js';
import IValidateConditionOptions from '@kurtosys/types/helpers/ConditionalHelper/IValidateConditionOptions.js';
import { TConditionalHelperDependencies } from '@kurtosys/types/helpers/TConditionalHelperDependencies.js';
import { IExecutionOptions, IQueryOptions } from '@kurtosys/types/query/index.js';

import { getDateDifference } from '../commonUtils/date.js';
import { formatter } from '../commonUtils/formatter.js';
import { isArrayLike } from '../commonUtils/isArrayLike.js';
import { getValueFromStringNotation } from '../commonUtils/object.js';
import { isNullOrEmpty, isNullOrUndefined, isUndefined } from '../commonUtils/typeChecks.js';

import { TranslationHelper } from './TranslationHelper.js';

/**
 * The ConditionalHelper allows for a generic conditional object to be used to determine if certain conditions are met.
 */
export class ConditionalHelper {
	public dependencies: TConditionalHelperDependencies;
	public conditional: IConditional;
	public translationHelper: TranslationHelper | undefined;

	constructor(
		dependencies: TConditionalHelperDependencies,
		conditional: IConditional,
		translationHelper?: TranslationHelper,
	) {
		this.dependencies = dependencies;
		this.conditional = conditional;
		this.translationHelper = translationHelper;
	}
	matchesWithOptions(options: IMatchesWithOptions): boolean {
		let response = false;
		const { conditional = this.conditional, ...validateConditionOptions } = options;
		const { conditions, format } = conditional;
		const conditionValues: any[] = [];
		if (conditions) {
			for (const condition of conditions) {
				const conditionValue = this.validateConditionWithOptions(condition, validateConditionOptions);
				conditionValues.push(conditionValue);
			}
		}
		response = this.applyConditionFormat(conditionValues, conditions, format);
		return response;
	}
	matches(instance: any, conditional?: IConditional, executionOptions?: IExecutionOptions): boolean {
		return this.matchesWithOptions({ instance, conditional, executionOptions });
	}
	validateConditionFormat(format: string, keys?: string[]): boolean {
		const validCharacters = ['(', ')', '&', '|', ' ', '{', '}', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
		// Strip out the valid keys
		for (const key of keys || []) {
			format = format.split(`{${key}}`).join('');
		}
		const conditionalFormatChars = format.split('');
		let isValid = true;
		for (const char of conditionalFormatChars) {
			isValid = isValid && validCharacters.indexOf(char) > -1;
			if (!isValid) {
				break;
			}
		}
		return isValid;
	}
	orchestrateConditionFormat(conditionResults: boolean[], format: string): string {
		if (!format || format === '') {
			format = '';
			for (let i = 1; i <= conditionResults.length; i++) {
				if (i > 1) {
					format += ' && ';
				}
				format += `{${i}}`;
			}
		}
		format = format.toLowerCase();
		format = format.split('and').join('&&');
		format = format.split('or').join('||');
		const singleCharChecks = ['&', '|'];
		const chars = format.split('');
		for (let i = 0; i < chars.length; i++) {
			const currentChar = chars[i];
			for (const singleChar of singleCharChecks) {
				if (currentChar === singleChar) {
					if (i === 0) {
						chars.shift();
						i--;
					} else if (i === chars.length - 1) {
						chars.pop();
					} else {
						const previousChar = chars[i - 1];
						const nextChar = chars[i + 1];
						if (previousChar !== singleChar && nextChar !== singleChar) {
							chars.splice(i, 0, singleChar);
						}
					}
				}
			}
		}
		while (singleCharChecks.includes(chars[chars.length - 1])) {
			chars.pop();
		}
		format = chars.join('').trim();

		return format;
	}
	applyConditionFormat(conditionResults: boolean[], conditions: ICondition[], format: string): boolean {
		if (!conditionResults || conditionResults.length === 0) {
			return true;
		}
		let workingFormat = this.orchestrateConditionFormat(conditionResults, format);
		const conditionKeys = conditions
			.filter((condition): boolean => !!condition.key)
			.map((condition): string | undefined => condition.key);
		// Check only valid characters used with place holders in workingFormat ({0}, {1} etc.)
		let isValid = this.validateConditionFormat(workingFormat, conditionKeys);
		if (isValid) {
			const indexOffset = workingFormat.indexOf('{0}') > -1 ? 0 : 1;

			for (let i = 0; i < conditionResults.length; i++) {
				// Force the value into a boolean, so as to not let any unwanted values creeping through
				const resultString = (!!conditionResults[i]).toString();
				workingFormat = workingFormat.split(`{${i + indexOffset}}`).join(resultString);
				const condition = conditions && conditions.length > i && conditions[i];
				if (condition) {
					const { key } = condition;
					if (key) {
						workingFormat = workingFormat.split(`{${key}}`).join(resultString);
					}
				}
			}
			// Check no extra place holders in place
			const placeholderBraces = ['{', '}'];
			for (const char of placeholderBraces) {
				isValid = isValid && workingFormat.indexOf(char) === -1;
				if (!isValid) {
					break;
				}
			}
			if (isValid) {
				try {
					// eslint-disable-next-line no-eval
					isValid = eval(workingFormat);
				} catch (ex) {
					isValid = false;
				}
			} else {
				console.error(`invalid condition format without place holders: ${workingFormat}`);
			}
		} else {
			console.error(`invalid condition format with place holders: ${workingFormat}`);
		}
		return isValid;
	}
	arrayContains(collection: any[], search: any, isCaseSensitive = false): boolean {
		if (!isNullOrUndefined(collection) && !isNullOrUndefined(search)) {
			return (
				undefined !==
				collection.find((item) => {
					let valueString;
					let searchString;

					if (this.translationHelper) {
						valueString = this.translationHelper.translate(item);
						searchString = this.translationHelper.translate(search);
					}
					// Found a case where the translation helper would return a number,
					// thus negating the toString at the let declaration
					valueString = item.toString();
					searchString = search.toString()

					if (!isCaseSensitive) {
						valueString = valueString.toLowerCase();
						searchString = searchString.toLowerCase();
					}

					return valueString === searchString;
				})
			);
		}
		return false;
	}
	contains(value: any, search: any, isCaseSensitive = false): boolean {
		if (!isNullOrUndefined(value) && !isNullOrUndefined(search)) {
			let valueString = value.toString();
			let searchString = search.toString();

			if (this.translationHelper) {
				valueString = this.translationHelper.translate(valueString);
				searchString = this.translationHelper.translate(searchString);
			}

			if (!isCaseSensitive) {
				valueString = valueString.toLowerCase();
				searchString = searchString.toLowerCase();
			}

			return valueString.indexOf(searchString) > -1;
		}
		return false;
	}
	startsWith(value: any, search: any, isCaseSensitive = false): boolean {
		if (!isNullOrUndefined(value) && !isNullOrUndefined(search)) {
			if (!Array.isArray(search)) {
				search = [search];
			}
			return !!(search as any[]).find((s) => {
				let valueString = value.toString();
				let searchString = s.toString();

				if (!isCaseSensitive) {
					valueString = valueString.toLowerCase();
					searchString = searchString.toLowerCase();
				}

				if (this.translationHelper) {
					let translatedValueString = this.translationHelper.translate(value.toString());

					if (!isCaseSensitive) {
						translatedValueString = translatedValueString.toLowerCase();
					}

					return valueString.indexOf(searchString) === 0 || translatedValueString.indexOf(searchString) === 0;
				}

				return valueString.indexOf(searchString) === 0;
			});
		}
		return false;
	}
	endsWith(value: any, search: any, isCaseSensitive = false): boolean {
		if (!isNullOrUndefined(value) && !isNullOrUndefined(search)) {
			if (!Array.isArray(search)) {
				search = [search];
			}
			return !!(search as any[]).find((s) => {
				let valueString: string = value.toString();
				let searchString = s.toString();

				if (!isCaseSensitive) {
					valueString = valueString.toLowerCase();
					searchString = searchString.toLowerCase();
				}

				if (this.translationHelper) {
					let translatedValueString = this.translationHelper.translate(value.toString());

					if (!isCaseSensitive) {
						translatedValueString = translatedValueString.toLowerCase();
					}

					return (
						valueString.indexOf(searchString) === valueString.length - searchString.length ||
						translatedValueString.indexOf(searchString) ===
							translatedValueString.length - searchString.length
					);
				}

				return valueString.indexOf(searchString) === valueString.length - searchString.length;
			});
		}
		return false;
	}
	validateConditionWithOptions(condition: ICondition, options: IValidateConditionOptions): boolean {
		if (!condition || typeof condition !== 'object') {
			return false;
		}
		const {
			field,
			fieldQuery,
			fieldDateDifference,
			operator,
			isCaseSensitive,
			regexTestFlags,
			fieldFormat,
			valueFormat,
		} = condition;
		const workingOperator = operator ? operator.toLowerCase() : '=';
		let response = false;
		let value: any;
		if (!isUndefined(condition.value)) {
			value = condition.value;
		} else if (!isUndefined(condition.valueQuery) && options.executionOptions) {
			value = this.getQueryResponse(condition.valueQuery, options.executionOptions);
		}
		if (!isUndefined(valueFormat)) {
			value = formatter(valueFormat, value);
		}

		let translatedValue;
		if (
			this.translationHelper &&
			['!=', '=', 'includes', 'excludes', 'in', 'not-in'].indexOf(workingOperator) >= 0
		) {
			if (Array.isArray(value)) {
				translatedValue = value.map((item) => this.translationHelper.translate(item));
			} else {
				translatedValue = this.translationHelper.translate(value);
			}
		}

		let fieldValue = options.instance;
		if (field && options.instance) {
			fieldValue = getValueFromStringNotation(field, options.instance);
		} else if (fieldQuery && options.executionOptions) {
			fieldValue = this.getQueryResponse(fieldQuery, options.executionOptions);
		} else if (fieldDateDifference) {
			const { period, start, end, useNowForEndDate } = fieldDateDifference;
			if (period && start && (end || useNowForEndDate)) {
				const startDateRaw = this.getQueryResponse(fieldDateDifference.start, options.executionOptions);
				let endDateRaw;
				if (fieldDateDifference.useNowForEndDate) {
					endDateRaw = new Date().toUTCString();
				} else {
					endDateRaw = this.getQueryResponse(fieldDateDifference.end, options.executionOptions);
				}
				fieldValue = getDateDifference(endDateRaw, startDateRaw, fieldDateDifference.period);
			}
		}
		if (!isUndefined(fieldFormat)) {
			fieldValue = formatter(fieldFormat, fieldValue);
		}

		const valueIsDate = this.isDate(value);
		const fieldValueIsDate = this.isDate(fieldValue);
		const regex = new RegExp(value, regexTestFlags);

		switch (workingOperator) {
			case '!=':
				response =
					fieldValue !== value &&
					(translatedValue === undefined || this.translationHelper.translate(fieldValue) !== translatedValue);
				break;
			case '=':
				response =
					fieldValue === value ||
					(translatedValue !== undefined && this.translationHelper.translate(fieldValue) === translatedValue);
				break;
			case '>':
				if (!valueIsDate || !fieldValueIsDate) {
					response = parseFloat(fieldValue) > parseFloat(value);
				} else {
					response = fieldValue > value;
				}
				break;
			case '>=':
				if (!valueIsDate || !fieldValueIsDate) {
					response = parseFloat(fieldValue) >= parseFloat(value);
				} else {
					response = fieldValue >= value;
				}
				break;
			case '<':
				if (!valueIsDate || !fieldValueIsDate) {
					response = parseFloat(fieldValue) < parseFloat(value);
				} else {
					response = fieldValue < value;
				}

				break;
			case '<=':
				if (!valueIsDate || !fieldValueIsDate) {
					response = parseFloat(fieldValue) <= parseFloat(value);
				} else {
					response = fieldValue <= value;
				}
				break;
			case 'includes':
				if (Array.isArray(fieldValue)) {
					response =
						this.arrayContains(fieldValue, value, isCaseSensitive) ||
						(translatedValue !== undefined &&
							this.arrayContains(fieldValue, translatedValue, isCaseSensitive));
				} else {
					response =
						this.contains(fieldValue, value, isCaseSensitive) ||
						(translatedValue !== undefined && this.contains(fieldValue, translatedValue, isCaseSensitive));
				}
				break;
			case 'excludes':
				if (Array.isArray(fieldValue)) {
					response =
						!this.arrayContains(fieldValue, value, isCaseSensitive) &&
						(translatedValue === undefined ||
							!this.arrayContains(fieldValue, translatedValue, isCaseSensitive));
				} else {
					response =
						!this.contains(fieldValue, value, isCaseSensitive) &&
						(translatedValue === undefined || !this.contains(fieldValue, translatedValue, isCaseSensitive));
				}
				break;
			case 'in':
				if (isArrayLike(value)) {
					response =
						this.arrayContains(value, fieldValue, isCaseSensitive) ||
						this.arrayContains(translatedValue, fieldValue, isCaseSensitive);
				}
				break;
			case 'not-in':
				if (isArrayLike(value)) {
					response =
						!this.arrayContains(value, fieldValue, isCaseSensitive) &&
						!this.arrayContains(translatedValue, fieldValue, isCaseSensitive);
				}
				break;
			case 'defined':
				response = !isUndefined(fieldValue);
				break;
			case 'undefined':
				response = isUndefined(fieldValue);
				break;
			case 'not-null':
				response = fieldValue !== null;
				break;
			case 'null':
				response = fieldValue === null;
				break;
			case 'starts-with':
				response =
					this.startsWith(fieldValue, value, isCaseSensitive) ||
					this.startsWith(fieldValue, translatedValue, isCaseSensitive);
				break;
			case 'ends-with':
				response =
					this.endsWith(fieldValue, value, isCaseSensitive) ||
					this.endsWith(fieldValue, translatedValue, isCaseSensitive);
				break;
			case 'not-starts-with':
				response =
					!this.startsWith(fieldValue, value, isCaseSensitive) &&
					!this.startsWith(fieldValue, translatedValue, isCaseSensitive);
				break;
			case 'not-ends-with':
				response =
					!this.endsWith(fieldValue, value, isCaseSensitive) &&
					!this.endsWith(fieldValue, translatedValue, isCaseSensitive);
				break;
			case 'null-or-empty':
				response = isNullOrEmpty(fieldValue);
				break;
			case 'not-null-or-empty':
				response = !isNullOrEmpty(fieldValue);
				break;
			case 'regex-test':
				response = regex.test(fieldValue);
				break;
			default:
				response = false;
				break;
		}
		return response;
	}
	isDate(value: any): boolean {
		return typeof value === 'string' && !!value.match(/^\d{4}-\d{2}-\d{2}/);
	}
	validateCondition(condition: ICondition, instance: any): boolean {
		return this.validateConditionWithOptions(condition, { instance });
	}
	private getQueryResponse(options: IQueryOptions, executionOptions: IExecutionOptions): any {
		if (this.dependencies?.queryClass) {
			const query = new this.dependencies.queryClass(options);
			return query.execute(executionOptions);
		}
		return undefined;
	}
}
